import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchWithCSRF } from '../utils/csrfUtils';
import supabase from '../supabaseClient';

// Hardcoded admin user ID
const ADMIN_USER_ID = 'b9ba4796-1294-427c-9cad-58d7d6c215d0';

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
  },
  title: {
    color: '#333',
    marginBottom: '2rem',
    fontSize: '2rem',
  },
  formSection: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    color: '#52cd92',
    marginBottom: '1.5rem',
    fontSize: '1.5rem',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    color: '#333',
    fontWeight: '500',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    minHeight: '100px',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  urlInputContainer: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-end',
  },
  urlInput: {
    flex: 1,
  },
  parseButton: {
    backgroundColor: '#52cd92',
    color: 'white',
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    height: '46px',
  },
  parseButtonDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },
  nutritionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1rem',
  },
  ingredientItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    padding: '1rem',
    background: '#f8f9fa',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
  ingredientOriginal: {
    fontSize: '0.9rem',
    color: '#666',
    fontStyle: 'italic',
  },
  ingredientRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr auto auto auto',
    gap: '0.5rem',
    alignItems: 'center',
  },
  ingredientSelect: {
    padding: '0.5rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  ingredientInput: {
    padding: '0.5rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  removeButton: {
    backgroundColor: '#ff4d4d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '0.5rem 0.75rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
  stepItem: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
    marginBottom: '0.75rem',
    padding: '0.75rem',
    background: '#f8f9fa',
    borderRadius: '4px',
  },
  stepNumber: {
    fontWeight: 'bold',
    minWidth: '2rem',
    color: '#52cd92',
  },
  stepTextarea: {
    flex: 1,
    padding: '0.5rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '0.9rem',
    minHeight: '60px',
    resize: 'vertical',
  },
  submitButton: {
    backgroundColor: '#52cd92',
    color: 'white',
    padding: '1rem 2rem',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1.1rem',
    cursor: 'pointer',
    marginTop: '1rem',
  },
  errorMessage: {
    color: '#ff4d4d',
    padding: '1rem',
    background: '#fff0f0',
    borderRadius: '4px',
    marginBottom: '1rem',
  },
  successMessage: {
    color: '#52cd92',
    padding: '1rem',
    background: '#f0fff5',
    borderRadius: '4px',
    marginBottom: '1rem',
  },
  loadingOverlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    color: '#666',
  },
  previewImage: {
    maxWidth: '300px',
    maxHeight: '200px',
    objectFit: 'cover',
    borderRadius: '8px',
    marginTop: '0.5rem',
  },
  noMatchWarning: {
    color: '#ff9800',
    fontSize: '0.85rem',
    marginTop: '0.25rem',
  },
  createButton: {
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '0.4rem 0.75rem',
    cursor: 'pointer',
    fontSize: '0.8rem',
    marginLeft: '0.5rem',
  },
  noMatchRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '0.25rem',
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '2rem',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  modalTitle: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#333',
  },
  modalButtons: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1.5rem',
  },
  modalButton: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  jsonPreview: {
    backgroundColor: '#f5f5f5',
    padding: '1rem',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    whiteSpace: 'pre-wrap',
    maxHeight: '300px',
    overflow: 'auto',
  },
  // Unit conversion styles
  unitConversionBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#fff3e0',
    border: '1px solid #ffb74d',
    borderRadius: '4px',
    marginTop: '0.5rem',
    fontSize: '0.85rem',
  },
  conversionIcon: {
    color: '#f57c00',
    fontWeight: 'bold',
  },
  conversionText: {
    flex: 1,
    color: '#e65100',
  },
  acceptButton: {
    backgroundColor: '#4caf50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '0.25rem 0.5rem',
    cursor: 'pointer',
    fontSize: '0.75rem',
    marginLeft: '0.5rem',
  },
  conversionAccepted: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#e8f5e9',
    border: '1px solid #81c784',
    borderRadius: '4px',
    marginTop: '0.5rem',
    fontSize: '0.85rem',
    color: '#2e7d32',
  },
  conversionNote: {
    fontSize: '0.75rem',
    color: '#666',
    marginTop: '0.25rem',
    fontStyle: 'italic',
  },
  confidenceLow: {
    backgroundColor: '#fff8e1',
    borderColor: '#ffcc80',
  },
  confidenceHigh: {
    backgroundColor: '#e3f2fd',
    borderColor: '#64b5f6',
  },
  convertButton: {
    backgroundColor: '#7c4dff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '0.4rem 0.6rem',
    cursor: 'pointer',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  convertButtonDisabled: {
    backgroundColor: '#bdbdbd',
    cursor: 'not-allowed',
  },
};

function ImportRecipeFromUrl() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pgUser, loading: authLoading } = useAuth();
  
  // URL input state - check for URL in query params
  const [url, setUrl] = useState(searchParams.get('url') || '');
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState('');
  const [isParsed, setIsParsed] = useState(false);
  
  // Recipe data state
  const [recipeName, setRecipeName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [prepTime, setPrepTime] = useState('');
  const [cookTime, setCookTime] = useState('');
  const [servings, setServings] = useState('');
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [carbohydrate, setCarbohydrate] = useState('');
  const [fat, setFat] = useState('');
  
  // Ingredients state - each has { original, quantity, unit, parsed_name, notes, matches, selectedIngredientId }
  const [ingredients, setIngredients] = useState([]);
  
  // Steps state
  const [steps, setSteps] = useState([]);
  
  // All ingredients from database for dropdown
  const [allIngredients, setAllIngredients] = useState([]);
  
  // Submit state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  
  // Enhance steps state
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhanceError, setEnhanceError] = useState('');
  
  // Session token
  const [sessionToken, setSessionToken] = useState('');
  
  // Create ingredient modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createIngredientIndex, setCreateIngredientIndex] = useState(null);
  const [createIngredientName, setCreateIngredientName] = useState('');
  const [createUnitOfMeasurement, setCreateUnitOfMeasurement] = useState('');
  const [createPurchaseUnit, setCreatePurchaseUnit] = useState('');
  const [createPurchaseUnitQuantity, setCreatePurchaseUnitQuantity] = useState('');
  const [createNutritionFacts, setCreateNutritionFacts] = useState('');
  const [generatedIngredient, setGeneratedIngredient] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  
  // Unit conversion state
  const [convertingIndex, setConvertingIndex] = useState(null);
  
  // Enum options - fetched from API
  const [unitOfMeasurementOptions, setUnitOfMeasurementOptions] = useState([]);
  const purchaseUnitOptions = ['unit', 'bottle', 'bag', 'box', 'bunch', 'lbs', 'packet', 'jar', 'carton', 'container', 'stick', 'loaf', 'pack', 'can'];
  
  // Check admin access
  useEffect(() => {
    if (!authLoading && pgUser) {
      if (pgUser.id !== ADMIN_USER_ID) {
        navigate('/home');
      }
    }
  }, [pgUser, authLoading, navigate]);
  
  // Get session token and fetch all ingredients + unit options
  useEffect(() => {
    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          setSessionToken(session.access_token);
          const headers = { 'Authorization': `Bearer ${session.access_token}` };

          // Fetch ingredients and unit options in parallel
          const [ingredientsResponse, unitOptionsResponse] = await Promise.all([
            fetchWithCSRF('/api/v1/ingredient', { method: 'GET', headers }),
            fetchWithCSRF('/api/v1/ingredient/unit_of_measurement_enum', { method: 'GET', headers })
          ]);

          const ingredientsData = await ingredientsResponse.json();
          if (Array.isArray(ingredientsData)) {
            setAllIngredients(ingredientsData);
          }

          const unitOptionsData = await unitOptionsResponse.json();
          if (Array.isArray(unitOptionsData)) {
            setUnitOfMeasurementOptions(unitOptionsData.map(item => item.unit));
          }
        }
      } catch (error) {
        console.error('Error initializing:', error);
      }
    };
    init();
  }, []);
  
  // Auto-parse if URL is provided via query params
  useEffect(() => {
    const urlParam = searchParams.get('url');
    if (urlParam && sessionToken && !isParsed && !isParsing) {
      handleParseUrl();
    }
  }, [sessionToken, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Parse recipe from URL
  const handleParseUrl = async () => {
    if (!url.trim()) return;
    
    setIsParsing(true);
    setParseError('');
    setIsParsed(false);
    
    try {
      const response = await fetchWithCSRF('/api/v1/recipe/parse-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({ url: url.trim() })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to parse recipe');
      }
      
      // Populate form with parsed data
      const { parsed_recipe, ingredients: parsedIngredients } = data;
      
      setRecipeName(parsed_recipe.name || '');
      setDescription(parsed_recipe.description || '');
      setImageUrl(parsed_recipe.image_url || '');
      setPrepTime(parsed_recipe.prep_time?.toString() || '');
      setCookTime(parsed_recipe.cook_time?.toString() || '');
      setServings(parsed_recipe.servings?.toString() || '');
      
      if (parsed_recipe.nutrition) {
        setCalories(parsed_recipe.nutrition.calories?.toString() || '');
        setProtein(parsed_recipe.nutrition.protein?.toString() || '');
        setCarbohydrate(parsed_recipe.nutrition.carbohydrate?.toString() || '');
        setFat(parsed_recipe.nutrition.fat?.toString() || '');
      }
      
      // Process ingredients - auto-select best match and handle unit conversions
      const processedIngredients = parsedIngredients.map(ing => {
        const bestMatch = ing.matches?.[0];
        const unitConversion = bestMatch?.unitConversion;
        
        // If there's a conversion proposal, use the converted quantity
        // but keep the original for display purposes
        const hasConversion = unitConversion?.needed && unitConversion?.convertedQuantity;
        
        return {
          ...ing,
          selectedIngredientId: bestMatch?.id || null,
          selectedUnit: bestMatch?.unit_of_measurement || '',
          // Store original values from recipe
          originalQuantity: ing.quantity,
          originalUnit: ing.unit,
          // Store conversion data
          unitConversion: unitConversion || null,
          // Track if conversion was accepted (auto-accept high confidence ones)
          conversionAccepted: hasConversion && unitConversion?.confidence >= 0.9,
          // Use converted quantity if high confidence, otherwise keep original
          quantity: hasConversion && unitConversion?.confidence >= 0.9 
            ? unitConversion.convertedQuantity 
            : ing.quantity
        };
      });
      setIngredients(processedIngredients);
      
      // Process steps
      const processedSteps = parsed_recipe.instructions.map((instruction, index) => ({
        step_number: index + 1,
        instruction
      }));
      setSteps(processedSteps);
      
      setIsParsed(true);
      
    } catch (error) {
      console.error('Parse error:', error);
      setParseError(error.message);
    } finally {
      setIsParsing(false);
    }
  };
  
  // Update ingredient selection
  const handleIngredientChange = (index, field, value) => {
    setIngredients(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };
  
  // Handle ingredient selection - auto-populate unit from the ingredient's unit_of_measurement
  const handleIngredientSelect = (index, ingredientId) => {
    if (!ingredientId) {
      // Cleared selection
      handleIngredientChange(index, 'selectedIngredientId', null);
      handleIngredientChange(index, 'selectedUnit', '');
      return;
    }
    
    // Find the ingredient in matches first, then in allIngredients
    const ing = ingredients[index];
    let selectedIngredient = ing.matches?.find(m => m.id === ingredientId);
    if (!selectedIngredient) {
      selectedIngredient = allIngredients.find(i => i.id === ingredientId);
    }
    
    // Update both the selected ingredient and the unit
    setIngredients(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        selectedIngredientId: ingredientId,
        selectedUnit: selectedIngredient?.unit_of_measurement || ''
      };
      return updated;
    });
  };
  
  // Accept a unit conversion proposal
  const handleAcceptConversion = (index) => {
    setIngredients(prev => {
      const updated = [...prev];
      const ing = updated[index];
      if (ing.unitConversion?.convertedQuantity) {
        updated[index] = {
          ...ing,
          quantity: ing.unitConversion.convertedQuantity,
          conversionAccepted: true
        };
      }
      return updated;
    });
  };
  
  // Revert to original quantity (undo conversion)
  const handleRevertConversion = (index) => {
    setIngredients(prev => {
      const updated = [...prev];
      const ing = updated[index];
      updated[index] = {
        ...ing,
        quantity: ing.originalQuantity,
        conversionAccepted: false
      };
      return updated;
    });
  };
  
  // Request smart conversion for an ingredient
  const handleRequestConversion = async (index) => {
    const ing = ingredients[index];
    
    // Need an ingredient selected and a quantity to convert
    if (!ing.selectedIngredientId || !ing.originalQuantity) {
      return;
    }
    
    // Find the selected ingredient to get its unit
    let selectedIngredient = ing.matches?.find(m => m.id === ing.selectedIngredientId);
    if (!selectedIngredient) {
      selectedIngredient = allIngredients.find(i => i.id === ing.selectedIngredientId);
    }
    
    const fromUnit = ing.originalUnit || ing.unit;
    const toUnit = selectedIngredient?.unit_of_measurement;
    
    // No conversion needed if units match or missing
    if (!fromUnit || !toUnit || fromUnit === toUnit) {
      return;
    }
    
    setConvertingIndex(index);
    
    try {
      const response = await fetchWithCSRF('/api/v1/recipe/convert-unit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          ingredientName: ing.parsed_name || selectedIngredient?.name || 'ingredient',
          quantity: ing.originalQuantity,
          fromUnit,
          toUnit
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Conversion failed');
      }
      
      // Update the ingredient with conversion data
      setIngredients(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          unitConversion: data.conversion,
          // Auto-accept if high confidence
          conversionAccepted: data.conversion?.confidence >= 0.9,
          quantity: data.conversion?.confidence >= 0.9 
            ? data.conversion.convertedQuantity 
            : updated[index].quantity
        };
        return updated;
      });
      
    } catch (error) {
      console.error('Conversion error:', error);
      // Set error state on the ingredient
      setIngredients(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          unitConversion: {
            needed: true,
            fromUnit,
            toUnit,
            error: error.message
          }
        };
        return updated;
      });
    } finally {
      setConvertingIndex(null);
    }
  };
  
  // Remove ingredient
  const handleRemoveIngredient = (index) => {
    setIngredients(prev => prev.filter((_, i) => i !== index));
  };
  
  // Update step
  const handleStepChange = (index, value) => {
    setSteps(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], instruction: value };
      return updated;
    });
  };
  
  // Remove step
  const handleRemoveStep = (index) => {
    setSteps(prev => {
      const filtered = prev.filter((_, i) => i !== index);
      // Renumber steps
      return filtered.map((step, i) => ({ ...step, step_number: i + 1 }));
    });
  };
  
  // Enhance steps with AI
  const handleEnhanceSteps = async () => {
    if (steps.length === 0) return;
    
    setIsEnhancing(true);
    setEnhanceError('');
    
    try {
      const response = await fetchWithCSRF('/api/v1/recipe/enhance-steps', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          recipe_name: recipeName,
          servings: servings ? parseInt(servings, 10) : null,
          ingredients: ingredients.map(ing => ({ original: ing.original })),
          steps: steps.map(step => ({ instruction: step.instruction }))
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to enhance steps');
      }
      
      const { enhanced_steps } = await response.json();
      
      // Update steps with enhanced versions
      setSteps(enhanced_steps.map((instruction, index) => ({
        step_number: index + 1,
        instruction
      })));
      
    } catch (error) {
      console.error('Enhance error:', error);
      setEnhanceError(error.message);
    } finally {
      setIsEnhancing(false);
    }
  };
  
  // Open create ingredient modal
  const handleOpenCreateModal = (index) => {
    const ing = ingredients[index];
    setCreateIngredientIndex(index);
    setCreateIngredientName(ing.parsed_name || ing.original || '');
    setCreateUnitOfMeasurement(ing.selectedUnit || '');
    setCreatePurchaseUnit('');
    setCreatePurchaseUnitQuantity('');
    setCreateNutritionFacts('');
    setGeneratedIngredient(null);
    setCreateError('');
    setShowCreateModal(true);
  };
  
  // Generate ingredient with AI
  const handleGenerateIngredient = async () => {
    if (!createIngredientName.trim()) return;
    
    setIsGenerating(true);
    setCreateError('');
    
    try {
      const response = await fetchWithCSRF('/api/v1/ingredient/generate-with-ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          ingredientName: createIngredientName.trim(),
          hints: {
            unit_of_measurement: createUnitOfMeasurement || null,
            purchase_unit: createPurchaseUnit || null,
            purchase_unit_quantity: createPurchaseUnitQuantity ? parseFloat(createPurchaseUnitQuantity) : null,
            nutrition_facts: createNutritionFacts || null,
            notes: ingredients[createIngredientIndex]?.notes
          }
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate ingredient');
      }
      
      setGeneratedIngredient(data);
      
    } catch (error) {
      console.error('Generate error:', error);
      setCreateError(error.message);
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Create the ingredient in the database
  const handleCreateIngredient = async () => {
    if (!generatedIngredient) return;
    
    setIsCreating(true);
    setCreateError('');
    
    try {
      const response = await fetchWithCSRF('/api/v1/ingredient', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify(generatedIngredient)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create ingredient');
      }
      
      // Refresh ingredients list
      const refreshResponse = await fetchWithCSRF('/api/v1/ingredient', {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      const refreshData = await refreshResponse.json();
      if (Array.isArray(refreshData)) {
        setAllIngredients(refreshData);
        
        // Find the newly created ingredient and select it (this also sets the unit)
        const newIngredient = refreshData.find(i => i.name === generatedIngredient.name);
        if (newIngredient && createIngredientIndex !== null) {
          handleIngredientSelect(createIngredientIndex, newIngredient.id);
        }
      }
      
      // Close modal
      setShowCreateModal(false);
      setGeneratedIngredient(null);
      
    } catch (error) {
      console.error('Create error:', error);
      setCreateError(error.message);
    } finally {
      setIsCreating(false);
    }
  };
  
  // Submit recipe
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!recipeName.trim()) {
      setSubmitError('Recipe name is required');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');
    
    try {
      // Prepare recipe data
      const recipeData = {
        name: recipeName,
        description: description || null,
        prep_time: prepTime ? parseInt(prepTime, 10) : null,
        cook_time: cookTime ? parseInt(cookTime, 10) : null,
        servings: servings ? parseInt(servings, 10) : null,
        image_url: imageUrl || null,
        calories: calories ? parseFloat(calories) : null,
        protein: protein ? parseFloat(protein) : null,
        carbohydrate: carbohydrate ? parseFloat(carbohydrate) : null,
        fat: fat ? parseFloat(fat) : null,
        instructions: steps.map(s => s.instruction),
        is_vegetarian: false,
        is_vegan: false
      };
      
      // Prepare ingredients - only include ones with a selected ingredient
      const ingredientData = ingredients
        .filter(ing => ing.selectedIngredientId)
        .map(ing => ({
          ingredient_id: ing.selectedIngredientId,
          quantity: ing.quantity || 1,
          unit: ing.selectedUnit || 'units',
          notes: ing.notes || null
        }));
      
      // Prepare steps
      const stepData = steps.map((step, index) => ({
        step_number: index + 1,
        instruction: step.instruction
      }));
      
      const response = await fetchWithCSRF('/api/v1/recipe/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          recipe: recipeData,
          ingredients: ingredientData,
          steps: stepData
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to import recipe');
      }
      
      setSubmitSuccess(`Recipe imported successfully! Redirecting to edit page...`);
      
      // Redirect to edit page after a short delay
      setTimeout(() => {
        navigate(`/recipes/${data.recipe_id}/edit`);
      }, 1500);
      
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Loading state
  if (authLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingOverlay}>Loading...</div>
      </div>
    );
  }
  
  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Import Recipe from URL</h1>
      
      {/* URL Input Section */}
      <div style={styles.formSection}>
        <h2 style={styles.sectionTitle}>Recipe URL</h2>
        <div style={styles.urlInputContainer}>
          <div style={styles.urlInput}>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.example.com/recipe/..."
              style={styles.input}
              disabled={isParsing}
            />
          </div>
          <button
            onClick={handleParseUrl}
            disabled={isParsing || !url.trim()}
            style={{
              ...styles.parseButton,
              ...(isParsing || !url.trim() ? styles.parseButtonDisabled : {})
            }}
          >
            {isParsing ? 'Parsing...' : 'Parse Recipe'}
          </button>
        </div>
        {parseError && <div style={styles.errorMessage}>{parseError}</div>}
      </div>
      
      {/* Only show form if parsed */}
      {isParsed && (
        <form onSubmit={handleSubmit}>
          {/* Basic Info Section */}
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Basic Information</h2>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Recipe Name *</label>
              <input
                type="text"
                value={recipeName}
                onChange={(e) => setRecipeName(e.target.value)}
                style={styles.input}
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={styles.textarea}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Image URL</label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                style={styles.input}
              />
              {imageUrl && (
                <img src={imageUrl} alt="Recipe preview" style={styles.previewImage} />
              )}
            </div>
            
            <div style={styles.nutritionGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Prep Time (min)</label>
                <input
                  type="number"
                  value={prepTime}
                  onChange={(e) => setPrepTime(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Cook Time (min)</label>
                <input
                  type="number"
                  value={cookTime}
                  onChange={(e) => setCookTime(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Servings</label>
                <input
                  type="number"
                  value={servings}
                  onChange={(e) => setServings(e.target.value)}
                  style={styles.input}
                />
              </div>
            </div>
          </div>
          
          {/* Nutrition Section */}
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Nutrition (per serving)</h2>
            <div style={styles.nutritionGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Calories</label>
                <input
                  type="number"
                  value={calories}
                  onChange={(e) => setCalories(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Protein (g)</label>
                <input
                  type="number"
                  value={protein}
                  onChange={(e) => setProtein(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Carbs (g)</label>
                <input
                  type="number"
                  value={carbohydrate}
                  onChange={(e) => setCarbohydrate(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Fat (g)</label>
                <input
                  type="number"
                  value={fat}
                  onChange={(e) => setFat(e.target.value)}
                  style={styles.input}
                />
              </div>
            </div>
          </div>
          
          {/* Ingredients Section */}
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Ingredients ({ingredients.length})</h2>
            
            {ingredients.map((ing, index) => (
              <div key={index} style={styles.ingredientItem}>
                <div style={styles.ingredientOriginal}>
                  Original: "{ing.original}"
                </div>
                <div style={styles.ingredientRow}>
                  <select
                    value={ing.selectedIngredientId || ''}
                    onChange={(e) => handleIngredientSelect(index, e.target.value || null)}
                    style={styles.ingredientSelect}
                  >
                    <option value="">-- Select Ingredient --</option>
                    {/* Show matched ingredients first */}
                    {ing.matches?.length > 0 && (
                      <optgroup label="Best Matches">
                        {ing.matches.map(match => (
                          <option key={match.id} value={match.id}>
                            {match.name} ({Math.round(match.confidence * 100)}% match)
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <optgroup label="All Ingredients">
                      {allIngredients.map(dbIng => (
                        <option key={dbIng.id} value={dbIng.id}>
                          {dbIng.name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <input
                    type="number"
                    step="0.25"
                    value={ing.quantity || ''}
                    onChange={(e) => handleIngredientChange(index, 'quantity', parseFloat(e.target.value) || null)}
                    placeholder="Qty"
                    style={styles.ingredientInput}
                  />
                  <select
                    value={ing.selectedUnit || ''}
                    disabled
                    style={{
                      ...styles.ingredientInput,
                      backgroundColor: '#f5f5f5',
                      cursor: 'not-allowed'
                    }}
                    title="Unit is determined by the selected ingredient"
                  >
                    <option value="">-- Unit --</option>
                    {unitOfMeasurementOptions.map(unit => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={ing.notes || ''}
                    onChange={(e) => handleIngredientChange(index, 'notes', e.target.value)}
                    placeholder="Notes"
                    style={styles.ingredientInput}
                  />
                  {/* Smart Convert button - shows when units differ */}
                  {ing.selectedIngredientId && ing.originalUnit && ing.selectedUnit && 
                   ing.originalUnit !== ing.selectedUnit && !ing.conversionAccepted && (
                    <button
                      type="button"
                      onClick={() => handleRequestConversion(index)}
                      disabled={convertingIndex === index}
                      style={{
                        ...styles.convertButton,
                        ...(convertingIndex === index ? styles.convertButtonDisabled : {})
                      }}
                      title={`Convert ${ing.originalQuantity || ''} ${ing.originalUnit} to ${ing.selectedUnit}`}
                    >
                      {convertingIndex === index ? '...' : 'Convert'}
                    </button>
                  )}
                  {/* Placeholder to keep grid alignment when no convert button */}
                  {!(ing.selectedIngredientId && ing.originalUnit && ing.selectedUnit && 
                     ing.originalUnit !== ing.selectedUnit && !ing.conversionAccepted) && (
                    <div style={{ width: '60px' }}></div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleOpenCreateModal(index)}
                    style={styles.createButton}
                  >
                    + New
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveIngredient(index)}
                    style={styles.removeButton}
                  >
                    Remove
                  </button>
                </div>
                
                {/* Unit Conversion Proposal */}
                {ing.unitConversion?.needed && ing.selectedIngredientId && (
                  ing.conversionAccepted ? (
                    // Conversion was accepted - show confirmation
                    <div style={styles.conversionAccepted}>
                      <span>&#10003;</span>
                      <span>
                        Converted: {ing.originalQuantity} {ing.originalUnit} → {ing.quantity} {ing.selectedUnit}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRevertConversion(index)}
                        style={{
                          ...styles.acceptButton,
                          backgroundColor: '#9e9e9e',
                          marginLeft: 'auto'
                        }}
                      >
                        Undo
                      </button>
                    </div>
                  ) : ing.unitConversion?.convertedQuantity ? (
                    // Conversion available but not yet accepted
                    <div style={{
                      ...styles.unitConversionBanner,
                      ...(ing.unitConversion.confidence >= 0.8 ? styles.confidenceHigh : 
                         ing.unitConversion.confidence >= 0.5 ? {} : styles.confidenceLow)
                    }}>
                      <span style={styles.conversionIcon}>&#9888;</span>
                      <span style={styles.conversionText}>
                        Unit mismatch: Recipe says "{ing.originalQuantity} {ing.originalUnit}" 
                        → Proposed: <strong>{ing.unitConversion.convertedQuantity} {ing.selectedUnit}</strong>
                        {ing.unitConversion.confidence < 0.8 && (
                          <span style={{ opacity: 0.7 }}> ({Math.round(ing.unitConversion.confidence * 100)}% confidence)</span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleAcceptConversion(index)}
                        style={styles.acceptButton}
                      >
                        Accept
                      </button>
                    </div>
                  ) : ing.unitConversion?.error ? (
                    // Conversion failed
                    <div style={{...styles.unitConversionBanner, backgroundColor: '#ffebee', borderColor: '#ef9a9a'}}>
                      <span style={{...styles.conversionIcon, color: '#c62828'}}>&#10007;</span>
                      <span style={{...styles.conversionText, color: '#c62828'}}>
                        Could not convert {ing.originalUnit} to {ing.selectedUnit}. Please enter quantity manually.
                      </span>
                    </div>
                  ) : null
                )}
                
                {/* Conversion notes */}
                {ing.unitConversion?.notes && ing.conversionAccepted && (
                  <div style={styles.conversionNote}>
                    {ing.unitConversion.notes}
                  </div>
                )}
                
                {!ing.selectedIngredientId && (
                  <div style={styles.noMatchWarning}>
                    No ingredient selected - this will be skipped
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Steps Section */}
          <div style={styles.formSection}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ ...styles.sectionTitle, marginBottom: 0 }}>Steps ({steps.length})</h2>
              <button
                type="button"
                onClick={handleEnhanceSteps}
                disabled={isEnhancing || steps.length === 0}
                style={{
                  ...styles.createButton,
                  opacity: (isEnhancing || steps.length === 0) ? 0.6 : 1,
                  cursor: (isEnhancing || steps.length === 0) ? 'not-allowed' : 'pointer'
                }}
              >
                {isEnhancing ? 'Enhancing...' : 'Enhance with AI'}
              </button>
            </div>
            
            {enhanceError && (
              <div style={styles.errorMessage}>{enhanceError}</div>
            )}
            
            {steps.map((step, index) => (
              <div key={index} style={styles.stepItem}>
                <span style={styles.stepNumber}>{step.step_number}.</span>
                <textarea
                  value={step.instruction}
                  onChange={(e) => handleStepChange(index, e.target.value)}
                  style={styles.stepTextarea}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveStep(index)}
                  style={styles.removeButton}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          
          {/* Submit Section */}
          <div style={styles.formSection}>
            {submitError && <div style={styles.errorMessage}>{submitError}</div>}
            {submitSuccess && <div style={styles.successMessage}>{submitSuccess}</div>}
            
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                ...styles.submitButton,
                ...(isSubmitting ? styles.parseButtonDisabled : {})
              }}
            >
              {isSubmitting ? 'Importing...' : 'Import Recipe'}
            </button>
            
            <p style={{ marginTop: '1rem', color: '#666', fontSize: '0.9rem' }}>
              After importing, you'll be redirected to the edit page to add diets, cuisines, and other details.
            </p>
          </div>
        </form>
      )}
      
      {/* Create Ingredient Modal */}
      {showCreateModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Create New Ingredient</h2>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Ingredient Name *</label>
              <input
                type="text"
                value={createIngredientName}
                onChange={(e) => setCreateIngredientName(e.target.value)}
                style={styles.input}
                placeholder="e.g., Gluten-Free Pasta"
              />
            </div>
            
            <div style={styles.nutritionGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Unit of Measurement</label>
                <select
                  value={createUnitOfMeasurement}
                  onChange={(e) => setCreateUnitOfMeasurement(e.target.value)}
                  style={styles.input}
                >
                  <option value="">-- Select --</option>
                  {unitOfMeasurementOptions.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Purchase Unit</label>
                <select
                  value={createPurchaseUnit}
                  onChange={(e) => setCreatePurchaseUnit(e.target.value)}
                  style={styles.input}
                >
                  <option value="">-- Select --</option>
                  {purchaseUnitOptions.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Purchase Qty</label>
                <input
                  type="number"
                  value={createPurchaseUnitQuantity}
                  onChange={(e) => setCreatePurchaseUnitQuantity(e.target.value)}
                  style={styles.input}
                  placeholder="e.g., 16"
                />
              </div>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Nutrition Facts (optional - paste from packaging/website)</label>
              <textarea
                value={createNutritionFacts}
                onChange={(e) => setCreateNutritionFacts(e.target.value)}
                style={{ ...styles.textarea, minHeight: '80px' }}
                placeholder="e.g., Per 100g: Calories 350, Protein 12g, Carbs 70g, Fat 2g..."
              />
            </div>
            
            {!generatedIngredient && (
              <button
                type="button"
                onClick={handleGenerateIngredient}
                disabled={isGenerating || !createIngredientName.trim()}
                style={{
                  ...styles.parseButton,
                  ...(isGenerating || !createIngredientName.trim() ? styles.parseButtonDisabled : {})
                }}
              >
                {isGenerating ? 'Generating with AI...' : 'Generate with AI'}
              </button>
            )}
            
            {generatedIngredient && (
              <>
                <div style={{ marginTop: '1rem' }}>
                  <label style={styles.label}>Generated Ingredient (Review & Edit if needed)</label>
                  <textarea
                    value={JSON.stringify(generatedIngredient, null, 2)}
                    onChange={(e) => {
                      try {
                        setGeneratedIngredient(JSON.parse(e.target.value));
                        setCreateError('');
                      } catch {
                        setCreateError('Invalid JSON');
                      }
                    }}
                    style={{ ...styles.textarea, fontFamily: 'monospace', minHeight: '250px' }}
                  />
                </div>
                
                <div style={styles.modalButtons}>
                  <button
                    type="button"
                    onClick={handleCreateIngredient}
                    disabled={isCreating}
                    style={{
                      ...styles.modalButton,
                      backgroundColor: '#52cd92',
                      color: 'white',
                      ...(isCreating ? styles.parseButtonDisabled : {})
                    }}
                  >
                    {isCreating ? 'Creating...' : 'Create Ingredient'}
                  </button>
                  <button
                    type="button"
                    onClick={handleGenerateIngredient}
                    disabled={isGenerating}
                    style={{
                      ...styles.modalButton,
                      backgroundColor: '#2196F3',
                      color: 'white',
                    }}
                  >
                    Regenerate
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    style={{
                      ...styles.modalButton,
                      backgroundColor: '#ccc',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
            
            {createError && <div style={{ ...styles.errorMessage, marginTop: '1rem' }}>{createError}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default ImportRecipeFromUrl;
